import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { fetchEngageList } from "@/services/api/common";
import {
  getModuleTypeFromRoute,
  normalizeEngageList,
} from "@/utils/sitePopups";
import styles from "./EngagingPopup.module.css";
import { IoCloseOutline } from "react-icons/io5";

const SHOW_MS = 5000;
const INTERVAL_MS = 30000;
const CONFETTI_ICON = "/assets/icons/ConfettiIcon.svg";
const FALLBACK_ICON = "/assets/icons/flighticon.svg";

function EngageThumbnail({ src, alt = "" }) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;

  return (
    <div className={styles.mediaWrap}>
      {showImage ? (
        <img
          src={src}
          alt={alt}
          className={styles.thumbnail}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className={styles.thumbnailFallback} aria-hidden="true">
          <img src={FALLBACK_ICON} alt="" className={styles.fallbackIcon} />
        </span>
      )}
    </div>
  );
}

export default function EngagingPopup() {
  const router = useRouter();
  const [items, setItems] = useState([]);
  const [visible, setVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const hideTimerRef = useRef(null);
  const intervalRef = useRef(null);

  const moduleType = useMemo(
    () => getModuleTypeFromRoute(router.asPath || router.pathname || "/"),
    [router.asPath, router.pathname]
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetchEngageList(moduleType);
        if (cancelled) return;
        setItems(normalizeEngageList(response));
        setActiveIndex(0);
      } catch {
        if (!cancelled) setItems([]);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [moduleType]);

  useEffect(() => {
    if (!items.length) {
      setVisible(false);
      return undefined;
    }

    const showNext = () => {
      setActiveIndex((prev) => (prev + 1) % items.length);
      setVisible(true);

      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = window.setTimeout(() => {
        setVisible(false);
      }, SHOW_MS);
    };

    intervalRef.current = window.setInterval(showNext, INTERVAL_MS);

    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
      setVisible(false);
    };
  }, [items]);

  const activeItem = items[activeIndex];
  if (!activeItem) return null;

  const cardClassName = [
    styles.card,
    visible ? styles.visible : "",
    activeItem.redirectionLink ? styles.cardLink : "",
  ]
    .filter(Boolean)
    .join(" ");

  const cardContent = (
    <>
      <EngageThumbnail
        key={activeItem.id || activeIndex}
        src={activeItem.thumbnail}
      />

      <div className={styles.content}>
        <div className={styles.contentMain}>
          {activeItem.title ? <p className={styles.title}>{activeItem.title}</p> : null}
          {activeItem.description ? (
            <p className={styles.description}>{activeItem.description}</p>
          ) : null}
        </div>
        {activeItem.timeAgo ? <p className={styles.meta}>{activeItem.timeAgo}</p> : null}
      </div>
      <div className={styles.closeIcon} onClick={() => setVisible(false)}>
        <IoCloseOutline size={18} />
      </div>

      <img
        src={CONFETTI_ICON}
        alt=""
        className={styles.confetti}
        aria-hidden="true"
        loading="lazy"
      />
    </>
  );

  return (
    <div className={styles.root} aria-live="polite" aria-atomic="true">
      {activeItem.redirectionLink ? (
        <a
          href={activeItem.redirectionLink}
          className={cardClassName}
          target="_blank"
          rel="noopener noreferrer"
        >
          {cardContent}
        </a>
      ) : (
        <div className={cardClassName}>{cardContent}</div>
      )}
    </div>
  );
}
